import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBell, faClock, faInfoCircle, faGift, faBullhorn, faExclamationTriangle, 
  faPercentage, faEnvelope, faQuestionCircle, faStar, faTrophy, faUserGraduate, 
  faCheckCircle, faTimesCircle, faTimes
} from "@fortawesome/free-solid-svg-icons";
import { faCalendarDays } from "@fortawesome/free-regular-svg-icons";
import { baseURL, ACTIVE_ANNOUNCEMENTS } from "../../API/API";
import "./DynamicAnnouncement.css";

// خريطة الأيقونات المتاحة للاختيار
const iconMap = {
  faBell,
  faClock,
  faInfoCircle,
  faGift,
  faBullhorn,
  faExclamationTriangle,
  faPercentage,
  faEnvelope,
  faQuestionCircle,
  faStar,
  faTrophy,
  faUserGraduate,
  faCheckCircle,
  faTimesCircle,
  faCalendarDays
};

export default function DynamicAnnouncement() {
  const [announcement, setAnnouncement] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const timerRef = useRef(null);

  // جلب الإعلانات النشطة
  useEffect(() => {
    axios
      .get(`${baseURL}/${ACTIVE_ANNOUNCEMENTS}`)
      .then((res) => {
        const list = res.data?.data || [];
        if (list.length > 0) {
          const latest = list[0]; // أحدث إعلان نشط
          
          // تحقق مما إذا كان المستخدم قد أغلق هذا الإعلان مسبقاً في هذه الجلسة
          const isClosed = sessionStorage.getItem(`closed_announcement_${latest.id}`);
          if (!isClosed) {
            setAnnouncement(latest);
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching active announcements:", err);
      });
  }, []);

  // حساب وقت العداد التنازلي
  useEffect(() => {
    if (!announcement) return;

    const isCountdown = 
      announcement.type === "countdown" || 
      announcement.display_type === "countdown";

    if (isCountdown && announcement.end_date) {
      const calculateTime = () => {
        // تهيئة التاريخ بشكل متوافق مع كافة المتصفحات
        const targetDateStr = announcement.end_date.replace(/-/g, "/");
        const difference = +new Date(targetDateStr) - +new Date();
        
        if (difference <= 0) {
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          if (timerRef.current) clearInterval(timerRef.current);
          return;
        }

        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      };

      calculateTime();
      timerRef.current = setInterval(calculateTime, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [announcement]);

  if (!announcement || !isVisible) return null;

  const isCountdown = 
    announcement.type === "countdown" || 
    announcement.display_type === "countdown";

  const showIcon = announcement.type === "icon" && announcement.icon;
  const currentIcon = showIcon ? iconMap[announcement.icon] : null;

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem(`closed_announcement_${announcement.id}`, "true");
  };

  // إعدادات الحركة والسرعة والاتجاه
  const isSticky = announcement.is_sticky === 1 || announcement.is_sticky === true;
  const speedClass = `speed-${announcement.scroll_speed || "normal"}`;
  const directionClass = `dir-${announcement.scroll_direction || "right"}`;

  return (
    <div className={`dynamic-announcement-container ${isCountdown ? "mode-countdown" : "mode-ticker"}`} dir="rtl">
      <div className="announcement-wrapper container">
        
        {/* زر الإغلاق الأنيق */}
        <button className="announcement-close-btn" onClick={handleClose} aria-label="إغلاق الإعلان">
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {isCountdown ? (
          // ==================== نمط العداد التنازلي ====================
          <div className="countdown-banner-content">
            <div className="countdown-info">
              {announcement.icon && iconMap[announcement.icon] && (
                <span className="countdown-icon-box animate-pulse">
                  <FontAwesomeIcon icon={iconMap[announcement.icon]} />
                </span>
              )}
              <h4 className="countdown-title">{announcement.content}</h4>
            </div>
            
            <div className="countdown-timer-wrapper">
              <div className="timer-block">
                <span className="timer-number">{String(timeLeft.days).padStart(2, "0")}</span>
                <span className="timer-label">يوم</span>
              </div>
              <span className="timer-separator">:</span>
              <div className="timer-block">
                <span className="timer-number">{String(timeLeft.hours).padStart(2, "0")}</span>
                <span className="timer-label">ساعة</span>
              </div>
              <span className="timer-separator">:</span>
              <div className="timer-block">
                <span className="timer-number">{String(timeLeft.minutes).padStart(2, "0")}</span>
                <span className="timer-label">دقيقة</span>
              </div>
              <span className="timer-separator">:</span>
              <div className="timer-block seconds">
                <span className="timer-number">{String(timeLeft.seconds).padStart(2, "0")}</span>
                <span className="timer-label">ثانية</span>
              </div>
            </div>
          </div>
        ) : (
          // ==================== نمط الشريط بيلف (Marquee Ticker) ====================
          <div className={`ticker-banner-content ${isSticky ? "sticky" : "scroll"}`}>
            {isSticky ? (
              <div className="ticker-text-wrapper justify-content-center">
                {currentIcon && (
                  <span className="ticker-icon animate-bounce-slow">
                    <FontAwesomeIcon icon={currentIcon} />
                  </span>
                )}
                <span className="ticker-message">{announcement.content}</span>
              </div>
            ) : (
              <div className="marquee-wrapper">
                <div className={`marquee-track ${speedClass} ${directionClass}`}>
                  <div className="marquee-content">
                    {Array(4).fill(0).map((_, i) => (
                      <span key={i} className="marquee-item">
                        {currentIcon && (
                          <span className="ticker-icon">
                            <FontAwesomeIcon icon={currentIcon} />
                          </span>
                        )}
                        <span className="ticker-message">{announcement.content}</span>
                        <span className="marquee-separator">✦</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
